import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Brain, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react';

interface AIConsentModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const AI_CONSENT_KEY = 'fiifit_ai_consent_accepted';
const AI_CONSENT_VERSION = '1.0'; // Increment when consent text changes

export function hasAIConsent(): boolean {
  try {
    const consent = localStorage.getItem(AI_CONSENT_KEY);
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.version === AI_CONSENT_VERSION && parsed.accepted === true;
  } catch {
    return false;
  }
}

export function setAIConsent(accepted: boolean): void {
  localStorage.setItem(AI_CONSENT_KEY, JSON.stringify({
    version: AI_CONSENT_VERSION,
    accepted,
    timestamp: new Date().toISOString()
  }));
}

export function revokeAIConsent(): void {
  localStorage.removeItem(AI_CONSENT_KEY);
}

export default function AIConsentModal({ isOpen, onAccept, onDecline }: AIConsentModalProps) {
  const [understood, setUnderstood] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    setAIConsent(true);
    onAccept();
  };

  const handleDecline = () => {
    onDecline();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Consimțământ Servicii AI</h2>
              <p className="text-sm text-blue-100">Informații importante despre datele tale</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* What data is sent */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Ce date sunt trimise?
            </h3>
            <p className="text-sm text-blue-800 leading-relaxed">
              Pentru a genera recomandări personalizate, aplicația FII FIT trimite următoarele date către serviciul AI:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>Profilul tău fitness: obiective, greutate, înălțime, preferințe</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>Date biometrice: vârstă, sex, greutate, înălțime</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>Obiectivele tale de sănătate și stilul de viață</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>Mesajele pe care le trimiți în chat-urile AI</span>
              </li>
            </ul>
          </div>

          {/* Who receives the data */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              Cui sunt trimise datele?
            </h3>
            <p className="text-sm text-purple-800 leading-relaxed">
              Datele sunt procesate de <strong>FII FIT AI</strong>, 
              furnizorul serviciului de inteligență artificială. FII FIT AI acționează ca 
              <strong> persoană împuternicită de operator</strong> și este obligat contractual să:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-purple-800">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>Nu utilizeze datele tale pentru antrenarea modelelor AI</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>Proceseze datele conform GDPR și standardelor de securitate</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>Șteargă datele după procesare (retenție maximă 30 zile)</span>
              </li>
            </ul>
          </div>

          {/* Why */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <h3 className="font-semibold text-green-900 mb-2">De ce este necesar?</h3>
            <p className="text-sm text-green-800 leading-relaxed">
              Funcționalitățile AI ale FII FIT (chat nutriționist, planuri alimentare, exerciții personalizate, 
              chat terapeutic) necesită procesarea datelor tale de sănătate pentru a genera recomandări 
              relevante și personalizate. Fără acest consimțământ, aceste funcționalități nu pot fi utilizate.
            </p>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-1">Important</h3>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Poți retrage acest consimțământ în orice moment din <strong>Setări → Confidențialitate</strong>. 
                  Retragerea nu afectează legalitatea prelucrării efectuate anterior.
                </p>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-wrap gap-3 text-sm">
            <Link 
              to="/privacy" 
              target="_blank" 
              className="text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
            >
              Politica de Confidențialitate <ExternalLink className="w-3 h-3" />
            </Link>
            <Link 
              to="/terms" 
              target="_blank" 
              className="text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
            >
              Termeni și Condiții <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          {/* Checkbox */}
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
            <input
              id="ai-consent-check"
              type="checkbox"
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
            />
            <label htmlFor="ai-consent-check" className="text-sm text-gray-700 leading-relaxed">
              Am citit și înțeleg că datele mele de sănătate vor fi trimise către <strong>OpenAI</strong> pentru 
              a genera recomandări personalizate. Sunt de acord cu această prelucrare.
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAccept}
            disabled={!understood}
            className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Accept și Continuă
          </button>
          <button
            onClick={handleDecline}
            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Refuz
          </button>
        </div>
      </div>
    </div>
  );
}
